import React, {
  useCallback,
  useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  useLocation, Link, useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import { AiFillDelete } from 'react-icons/ai';
import { PAGE_RESET } from '../../actions/types';
import {
  createPage, deletePage, getPage, updatePage,
} from '../../actions/pages';
import { get, set } from '../../utils';
import Field from '../../components/Field';
import { PageField, PageTemplate } from '../../types/page';
import styles from './page.module.scss';

const Page = () => {
  const { id: pageId, type } = useParams();
  const [searchParams] = useSearchParams();
  const parent = searchParams.get('parent');
  const pages = searchParams.get('pages');
  const containerRef = useRef<HTMLDivElement>(null);
  const [isSaved, setIsSaved] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { page, list } = useSelector((store: any) => store).pagesReducer;

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<FieldValues>({
    defaultValues: {
      draft: true,
      type,
      datePublication: new Date().toISOString(),
    },
  });
  const template = list?.templates?.find(
    (t: PageTemplate) => t.type === type,
  );
  const fields : PageField[] | null = template?.fields || [];

  // Navigate to page when create
  useEffect(() => {
    if (page?._id && /create/.test(location.pathname)) {
      navigate(`/pages/${page.type}/${page._id}`);
    }
  }, [page?._id]);

  async function handleDelete() {
    // eslint-disable-next-line no-alert
    if (window.confirm('Veuillez confirmer la suppression de la page.')) {
      await deletePage(dispatch, page);
    }
    navigate(`/pages/${type}`);
  }

  function onSubmit(values: FieldValues) {
    if (page?._id) {
      return updatePage(dispatch, values);
    }
    const data = { ...values };
    if (parent) {
      data.content = { ...data.content, parent };
    }
    if (pages) {
      data.pages = pages;
    }
    return createPage(dispatch, data);
  }

  useLayoutEffect(() => {
    containerRef?.current?.parentElement?.scrollTo({
      top: 0,
    });
    dispatch({
      type: PAGE_RESET,
    });
  }, [type]);

  useEffect(() => {
    if (pageId) {
      getPage(dispatch, pageId);
    }
  }, [pageId, type]);

  useEffect(() => {
    if (page?._id) {
      reset(page);
      setIsSaved(true);
    } else {
      reset({
        draft: true,
        metadesc: '',
        ogDesc: '',
        type,
        datePublication: new Date().toISOString(),
      });
    }
  }, [page, type]);

  useEffect(() => {
    setIsSaved(!isDirty);
  }, [isDirty]);

  // Keep only required fields if new page
  const filteredFields = useMemo(() => (fields || []).filter((f: PageField) => (
    page?._id || (!page && f.required)
  )), [fields, page?._id]);

  const getDefaultFieldValue = useCallback((name: string) => {
    const value = get(page, name) || null;
    return value;
  }, [page]);

  return (
    <div
      id='page'
      className={styles.page}
      ref={containerRef}
    >

      <div className={styles.header}>
        <h1>{page?.content?.title || template?.create}</h1>
        {page?.content?.parent?._id && <Link
          to={`/pages/${page?.content?.parent?.type}/${page?.content?.parent?._id}`}
        >
          {page?.content?.parent?.content?.title}
        </Link>}
      </div>

      <div className={styles.submit}>
        {type === 'article'
          && <a href={`${process.env.REACT_APP_FRONT_URL}/blog-bakamag/${page?.slug}`} target="_blank" rel="noreferrer">
            Voir l&apos;article
          </a>
        }
        {type === 'use-case'
          && <a href={`${process.env.REACT_APP_FRONT_URL}/use-case/${page?.slug}`} target="_blank" rel="noreferrer">
            Voir le cas d&apos;usage
          </a>
        }
        {page?._id
          && <button className={`${styles.btn} ${watch('draft') ? styles.draft : styles.success}`}
            onClick={() => updatePage(dispatch, { ...watch(), draft: !watch('draft') })}
          >
            {watch('draft') ? 'Brouillon' : 'Publié'}
          </button>
        }
        <button className={`${styles.btn} ${isSaved ? styles.saved : ''}`} onClick={handleSubmit(onSubmit)}>
          Sauvegarder
        </button>
      </div>
      <div className={styles.form}>
        <div className={styles.content}>
          {filteredFields?.filter((f) => f.category === 'content')?.map((f: PageField) => <Field
            key={f.name}
            field={f}
            defaultValue={getDefaultFieldValue(f.name) || null}
            errors={errors}
            control={control}
            watch={watch}
            setValue={setValue}
            pageId={page?._id}
            page={page}
            setIsSaved={setIsSaved}
            submit={(obj) => {
              const keys = Object.keys(obj);
              const values = getValues();
              const data : any = JSON.parse(JSON.stringify(values));
              keys.forEach((k : string) => {
                set(data, k, obj[k]);
              });
              updatePage(dispatch, data);
            }}
          />)}
        </div>
        <div className={styles.meta}>
          {filteredFields?.filter((f) => f.category === 'meta')?.map((f: PageField) => <Field
            key={f.name}
            field={f}
            defaultValue={getDefaultFieldValue(f.name) || null}
            errors={errors}
            control={control}
            watch={watch}
            setValue={setValue}
            page={page}
            pageId={page?._id}
            submit={(obj) => {
              const keys = Object.keys(obj);
              const values = getValues();
              const data : any = JSON.parse(JSON.stringify(values));
              keys.forEach((k : string) => {
                set(data, k, obj[k]);
              });
              updatePage(dispatch, data);
            }}
          />)}
          {page?._id
            && <button
              className={styles.delete}
              onClick={() => handleDelete()}
            >
              <AiFillDelete />Supprimer la page
            </button>
          }
        </div>
      </div>
    </div>
  );
};

export default Page;
